import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { uploadFile, addEvidenceBulk } from "@/lib/api";
import { UploadCloud, Loader2, CheckCircle2, AlertCircle, Layers, FileCheck2 } from "lucide-react";
import { toast } from "sonner";

const field = "text-xs text-neutral-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border file:border-white/15 file:bg-black file:text-neutral-300 file:text-xs";

export function BulkIntakeDialog({ caseId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]); // {name, status, file_id, sha256}
  const [dragOver, setDragOver] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [summary, setSummary] = useState(null);

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setSummary(null);
    const base = items.length;
    setItems((prev) => [...prev, ...files.map((f) => ({ name: f.name, status: "uploading", file_id: null, sha256: null }))]);
    await Promise.all(files.map(async (f, i) => {
      try {
        const data = await uploadFile(f);
        setItems((prev) => prev.map((it, idx) => idx === base + i
          ? { ...it, status: "done", file_id: data.file_id, sha256: data.sha256, name: data.filename || f.name } : it));
      } catch {
        setItems((prev) => prev.map((it, idx) => idx === base + i ? { ...it, status: "error" } : it));
      }
    }));
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); };

  const ready = items.filter((i) => i.status === "done");
  const uploading = items.some((i) => i.status === "uploading");

  const ingest = async () => {
    if (!ready.length) { toast.error("Upload at least one file first"); return; }
    setIngesting(true);
    try {
      const res = await addEvidenceBulk(caseId, ready.map((i) => ({ file_id: i.file_id, filename: i.name, sha256: i.sha256 })));
      setSummary(res);
      toast.success(`Ingested ${res.added} · skipped ${res.skipped} duplicate${res.skipped === 1 ? "" : "s"}`);
      await onAdded?.();
    } catch { toast.error("Bulk intake failed"); }
    finally { setIngesting(false); }
  };

  const reset = () => { setItems([]); setSummary(null); };
  const close = (o) => { setOpen(o); if (!o) reset(); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogTrigger asChild>
        <button data-testid="bulk-intake-btn" className="flex items-center gap-2 bg-monk-blue text-white text-sm font-medium rounded-sm px-3 py-2 hover:bg-monk-blue/90 transition-colors">
          <Layers className="w-4 h-4" /> Bulk Intake
        </button>
      </DialogTrigger>
      <DialogContent className="bg-monk-panel border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Bulk Evidence Intake</DialogTitle>
          <DialogDescription className="text-neutral-500 text-xs">
            Drop scans, documents or photos. Identical files are skipped by SHA-256 hash. Each new item is
            auto-triaged by Monk and routed to the relevant forensic engines — findings live in Evidence Detail.
          </DialogDescription>
        </DialogHeader>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          data-testid="bulk-dropzone"
          className={`rounded-md border border-dashed p-6 text-center transition-colors ${dragOver ? "border-monk-blue bg-monk-blue/10" : "border-white/20 bg-black/40"}`}
        >
          <UploadCloud className="w-7 h-7 text-neutral-500 mx-auto mb-2" />
          <p className="text-sm text-neutral-300 mb-1">Drag & drop files here</p>
          <p className="hunch-data text-[12px] mb-3">or select multiple files below</p>
          <input data-testid="bulk-file-input" type="file" multiple onChange={(e) => addFiles(e.target.files)} className={field} />
        </div>

        {items.length > 0 && (
          <div className="max-h-52 overflow-auto space-y-1.5 mt-1" data-testid="bulk-file-list">
            {items.map((it, idx) => (
              <div key={idx} className="flex items-center gap-2 text-[12px] bg-black/40 border border-white/10 rounded-sm px-2.5 py-1.5">
                {it.status === "uploading" && <Loader2 className="w-3.5 h-3.5 animate-spin text-monk-blue shrink-0" />}
                {it.status === "done" && <FileCheck2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                {it.status === "error" && <AlertCircle className="w-3.5 h-3.5 text-monk-critical shrink-0" />}
                <span className="truncate flex-1 text-neutral-300 font-mono">{it.name}</span>
                <span className="text-[9px] font-mono text-neutral-500 uppercase">{it.status}</span>
              </div>
            ))}
          </div>
        )}

        {summary && (
          <div data-testid="bulk-summary" className="flex items-center gap-3 text-[12px] font-mono bg-emerald-500/5 border border-emerald-500/20 rounded-sm px-3 py-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-300">Added {summary.added}</span>
            <span className="text-neutral-500">·</span>
            <span className="text-neutral-400">Skipped {summary.skipped} duplicate{summary.skipped === 1 ? "" : "s"}</span>
          </div>
        )}

        <button onClick={ingest} disabled={ingesting || uploading || ready.length === 0} data-testid="bulk-ingest-btn"
          className="w-full bg-monk-blue text-white rounded-sm py-2.5 text-sm font-medium flex items-center justify-center gap-2 hover:bg-monk-blue/90 disabled:opacity-50 transition-colors">
          {ingesting ? <><Loader2 className="w-4 h-4 animate-spin" /> Ingesting…</> : `Ingest ${ready.length || ""} File${ready.length === 1 ? "" : "s"}`.trim()}
        </button>
      </DialogContent>
    </Dialog>
  );
}
